import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, Plus, X as XIcon, Sun, Moon, Trash2, LogIn } from "lucide-react";
import PageHeader from "../components/PageHeader";
import { useIstanti, imageUrlFor } from "../lib/useIstanti";

/**
 * TIME Watch → Utility → Istanti (cloud edition)
 *
 * Due Istanti al giorno (morning ≤11:59 / evening ≥12:00).
 * Ogni foto:
 *   • ridimensionata client-side (max 1400px, JPEG 82%)
 *   • marchiata con Data · Ora · firma "TIME Watch" integrata nel pixel
 *   • uploadata via POST /api/istanti/upload (multipart)
 *   • recuperata via <img src="/api/istanti/{id}/image"> con cookie httpOnly
 *
 * Nessuna dipendenza da IndexedDB o localStorage: cambiare dispositivo o
 * pulire la cache non fa perdere nulla.
 */

const MAX_DIM = 1400;
const JPEG_QUALITY = 0.82;

const pad2 = (n) => String(n).padStart(2, "0");
const todayIso = (d = new Date()) =>
  `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
const toDisplayDate = (isoDate) => {
  const [y, m, d] = isoDate.split("-");
  return `${d}.${m}.${y}`;
};
const currentSlot = (d = new Date()) => (d.getHours() < 12 ? "morning" : "evening");
const nowTimeHHMM = (d = new Date()) => `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;

/**
 * Marchia l'immagine e la restituisce come Blob JPEG.
 * Firma elegante integrata nell'angolo inferiore destro:
 *   [ORA HH:MM]
 *   [DATA GG.MM.AAAA]
 *   [TIME Watch]  <— firma minimale, letter-spaced come eyebrow
 * Colore auto: bianco se sfondo scuro, quasi-nero se sfondo chiaro.
 */
async function brandImage(file, dateIso, timeHHMM) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const im = new Image();
    im.onload = () => resolve(im);
    im.onerror = () => reject(new Error("image load failed"));
    im.src = dataUrl;
  });

  const scale = Math.min(1, MAX_DIM / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement("canvas");
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, w, h);

  // Luminanza area angolo inf-destro
  const sampleW = Math.min(240, Math.floor(w * 0.35));
  const sampleH = Math.min(140, Math.floor(h * 0.22));
  const sx = w - sampleW;
  const sy = h - sampleH;
  let avg = 128;
  try {
    const d = ctx.getImageData(sx, sy, sampleW, sampleH).data;
    let sum = 0;
    let count = 0;
    for (let i = 0; i < d.length; i += 32) {
      sum += 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      count++;
    }
    avg = sum / Math.max(1, count);
  } catch {
    /* CORS: fallback */
  }
  const isDarkBg = avg <= 170;
  const inkColor = isDarkBg ? "rgba(255,255,255,0.92)" : "rgba(18,18,20,0.88)";
  const accentColor = isDarkBg ? "rgba(74,222,128,0.95)" : "rgba(0,120,80,0.95)";
  const shadowColor = isDarkBg ? "rgba(0,0,0,0.55)" : "rgba(255,255,255,0.55)";

  const baseSize = Math.max(14, Math.round(Math.min(w, h) * 0.028));
  const dateSize = Math.max(11, Math.round(baseSize * 0.7));
  const signSize = Math.max(10, Math.round(baseSize * 0.6));
  const padding = Math.round(Math.min(w, h) * 0.035);
  const dotSize = Math.max(4, Math.round(baseSize * 0.22));

  ctx.save();
  ctx.textAlign = "right";
  ctx.textBaseline = "bottom";
  ctx.shadowColor = shadowColor;
  ctx.shadowBlur = Math.round(baseSize * 0.4);
  ctx.fillStyle = inkColor;

  const xBase = w - padding;
  let y = h - padding;

  // Riga 1 (in basso): ORA — carattere prominente ma sottile
  ctx.font = `300 ${baseSize}px system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif`;
  ctx.fillText(timeHHMM, xBase, y);
  y -= baseSize + Math.round(baseSize * 0.25);

  // Riga 2: DATA
  ctx.font = `300 ${dateSize}px system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif`;
  ctx.fillText(toDisplayDate(dateIso), xBase, y);
  y -= dateSize + Math.round(baseSize * 0.55);

  // Divisorio: piccolo pallino verde TIME
  const dotY = y + Math.round(dotSize * 0.3);
  ctx.shadowBlur = 0;
  ctx.beginPath();
  ctx.arc(xBase - Math.round(signSize * 3.4), dotY, dotSize / 2, 0, Math.PI * 2);
  ctx.fillStyle = accentColor;
  ctx.fill();
  ctx.shadowColor = shadowColor;
  ctx.shadowBlur = Math.round(baseSize * 0.4);

  // Riga 3: firma "TIME Watch" — letter-spaced, minimal
  ctx.fillStyle = inkColor;
  ctx.font = `500 ${signSize}px system-ui, -apple-system, "Helvetica Neue", Arial, sans-serif`;
  ctx.textAlign = "right";
  ctx.fillText("TIME  Watch", xBase, y);

  ctx.restore();

  return await new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error("canvas.toBlob failed"))),
      "image/jpeg",
      JPEG_QUALITY
    );
  });
}

export default function TimeWatchIstantiPage() {
  const { items, loaded, uploadIstante, deleteIstante, isAuthenticated } = useIstanti();
  const [pending, setPending] = useState(null); // {slot, dateIso, timeHHMM, previewUrl, blob}
  const [savedFlash, setSavedFlash] = useState(false);
  const [viewerId, setViewerId] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const morningInputRef = useRef(null);
  const eveningInputRef = useRef(null);

  const today = todayIso();
  const nowSlot = currentSlot();
  const todayItems = items.filter((it) => it.date === today);
  const morningToday = todayItems.find((it) => it.slot === "morning");
  const eveningToday = todayItems.find((it) => it.slot === "evening");

  const archive = useMemo(() => {
    const byDate = new Map();
    for (const it of items) {
      if (!byDate.has(it.date)) byDate.set(it.date, {});
      byDate.get(it.date)[it.slot] = it;
    }
    return [...byDate.entries()]
      .sort((a, b) => (a[0] < b[0] ? 1 : -1))
      .map(([date, slots]) => ({ date, ...slots }));
  }, [items]);

  const openCameraFor = (slot) => {
    if (slot === "morning" && morningInputRef.current)
      morningInputRef.current.click();
    if (slot === "evening" && eveningInputRef.current)
      eveningInputRef.current.click();
  };

  const handleFile = async (slot, e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (!file) return;
    setError("");
    const now = new Date();
    const dateIso = todayIso(now);
    const timeHHMM = nowTimeHHMM(now);
    try {
      const blob = await brandImage(file, dateIso, timeHHMM);
      const previewUrl = URL.createObjectURL(blob);
      setPending({ slot, dateIso, timeHHMM, previewUrl, blob });
    } catch (err) {
      setError("Impossibile elaborare l'immagine.");
    }
  };

  const confirmPending = async () => {
    if (!pending) return;
    setBusy(true);
    setError("");
    try {
      await uploadIstante({
        blob: pending.blob,
        date: pending.dateIso,
        slot: pending.slot,
        timeHHMM: pending.timeHHMM,
      });
      URL.revokeObjectURL(pending.previewUrl);
      setPending(null);
      setSavedFlash(true);
      if (typeof navigator !== "undefined" && navigator.vibrate) {
        try {
          navigator.vibrate(20);
        } catch {
          /* ignore */
        }
      }
      setTimeout(() => setSavedFlash(false), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Upload fallito");
    } finally {
      setBusy(false);
    }
  };

  const cancelPending = () => {
    if (pending?.previewUrl) URL.revokeObjectURL(pending.previewUrl);
    setPending(null);
  };

  const viewerItem = viewerId ? items.find((it) => it.id === viewerId) : null;

  if (!isAuthenticated) {
    return (
      <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-3xl mx-auto">
        <Link
          to="/innovazione/time-watch/utility"
          data-testid="time-watch-istanti-back-link"
          className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
        >
          <ChevronLeft className="w-3.5 h-3.5" />
          Utility
        </Link>
        <PageHeader
          eyebrow="Utility"
          title="ISTANTE"
          description="Per conservare i tuoi Istanti in modo permanente devi accedere al tuo account."
          testId="time-watch-istanti-header"
        />
        <div className="glass-card p-8 text-center" data-testid="istanti-need-auth">
          <p className="text-white/70 font-light mb-6 leading-relaxed">
            Ogni Istante viene collegato al tuo account e conservato nel cloud.
            Anche cambiando telefono o cancellando la cache del browser, ritrovi
            sempre tutti i tuoi ricordi.
          </p>
          <Link
            to="/auth"
            data-testid="istanti-goto-login"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-emerald-400/90 text-black text-[11px] tracking-[0.24em] uppercase font-medium hover:bg-emerald-400 transition"
          >
            <LogIn className="w-4 h-4" />
            Accedi
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="px-5 sm:px-8 lg:px-12 py-16 sm:py-20 max-w-4xl mx-auto">
      <Link
        to="/innovazione/time-watch/utility"
        data-testid="time-watch-istanti-back-link"
        className="inline-flex items-center gap-2 text-[11px] tracking-[0.28em] uppercase text-white/40 hover:text-white/80 transition mb-10"
      >
        <ChevronLeft className="w-3.5 h-3.5" />
        Utility
      </Link>

      <PageHeader
        eyebrow="Utility"
        title="ISTANTE"
        description={
          <>
            <span className="text-emerald-300">TIME</span>
            <br />
            Quali sono i due Istanti che oggi meritano di essere ricordati?
          </>
        }
        testId="time-watch-istanti-header"
      />

      {savedFlash && (
        <div
          data-testid="istanti-saved-toast"
          className="mb-6 glass-card p-4 border-emerald-400/50 text-center text-emerald-200 text-sm font-light animate-pulse"
        >
          Istante conservato nel cloud.
        </div>
      )}

      {error && (
        <div
          data-testid="istanti-error"
          className="mb-6 glass-card p-4 border-red-400/50 text-center text-red-300 text-sm font-light"
        >
          {error}
        </div>
      )}

      <input
        ref={morningInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFile("morning", e)}
        data-testid="istanti-input-morning"
        className="hidden"
      />
      <input
        ref={eveningInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={(e) => handleFile("evening", e)}
        data-testid="istanti-input-evening"
        className="hidden"
      />

      <div
        data-testid="istanti-today-grid"
        className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6 mb-14"
      >
        <SlotCard
          slot="morning"
          icon={<Sun className="w-4 h-4" />}
          title="Istante del Mattino"
          windowLabel="00:00 – 11:59"
          filled={morningToday}
          canCapture={loaded && !morningToday && nowSlot === "morning"}
          inactiveMessage={
            morningToday
              ? "Hai già conservato il tuo Istante del mattino."
              : "Disponibile domani mattina."
          }
          onOpenCamera={() => openCameraFor("morning")}
          onOpenViewer={(id) => setViewerId(id)}
          testId="slot-morning"
        />
        <SlotCard
          slot="evening"
          icon={<Moon className="w-4 h-4" />}
          title="Istante della Sera"
          windowLabel="12:00 – 23:59"
          filled={eveningToday}
          canCapture={loaded && !eveningToday && nowSlot === "evening"}
          inactiveMessage={
            eveningToday
              ? "Hai già conservato il tuo Istante della sera."
              : "Disponibile dalle 12:00."
          }
          onOpenCamera={() => openCameraFor("evening")}
          onOpenViewer={(id) => setViewerId(id)}
          testId="slot-evening"
        />
      </div>

      <div className="mb-6">
        <div className="text-[10px] tracking-[0.32em] uppercase text-white/40 mb-4">
          Archivio ({archive.length} {archive.length === 1 ? "giorno" : "giorni"})
        </div>
        {!loaded ? (
          <div className="text-white/40 font-light py-8 text-center text-[11px] tracking-[0.24em] uppercase">
            Caricamento…
          </div>
        ) : archive.length === 0 ? (
          <div
            data-testid="istanti-archive-empty"
            className="text-white/40 font-light py-8 text-center"
          >
            Ancora nessun Istante conservato.
          </div>
        ) : (
          <ul data-testid="istanti-archive" className="space-y-3">
            {archive.map((row) => (
              <li
                key={row.date}
                data-testid={`istanti-archive-row-${row.date}`}
                className="glass-card p-4 sm:p-5 flex items-center gap-4"
              >
                <div className="text-[11px] tracking-[0.32em] uppercase text-white/50 w-28 shrink-0">
                  {toDisplayDate(row.date)}
                </div>
                <div className="flex-1 grid grid-cols-2 gap-3">
                  <ArchiveThumb
                    label="Mattino"
                    item={row.morning}
                    onOpen={(id) => setViewerId(id)}
                    testId={`archive-thumb-morning-${row.date}`}
                  />
                  <ArchiveThumb
                    label="Sera"
                    item={row.evening}
                    onOpen={(id) => setViewerId(id)}
                    testId={`archive-thumb-evening-${row.date}`}
                  />
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {pending && (
        <PreviewDialog
          pending={pending}
          busy={busy}
          onCancel={cancelPending}
          onConfirm={confirmPending}
        />
      )}

      {viewerItem && (
        <Viewer
          item={viewerItem}
          onClose={() => setViewerId(null)}
          onDelete={async () => {
            try {
              await deleteIstante(viewerItem.id);
              setViewerId(null);
            } catch (e) {
              setError(e?.response?.data?.detail || e.message);
            }
          }}
        />
      )}
    </div>
  );
}

function SlotCard({
  icon,
  title,
  windowLabel,
  filled,
  canCapture,
  inactiveMessage,
  onOpenCamera,
  onOpenViewer,
  testId,
}) {
  return (
    <div data-testid={testId} className="glass-card p-5 sm:p-6">
      <div className="flex items-center gap-2 text-[10px] tracking-[0.32em] uppercase text-white/50 mb-4">
        {icon}
        <span>{title}</span>
        <span className="ml-auto text-white/25">{windowLabel}</span>
      </div>

      {filled ? (
        <button
          type="button"
          onClick={() => onOpenViewer(filled.id)}
          data-testid={`${testId}-thumb`}
          className="relative w-full aspect-[4/5] rounded-lg overflow-hidden border border-white/5 hover:border-white/15 transition group"
        >
          <img
            src={imageUrlFor(filled)}
            alt="Istante"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.02]"
          />
          <div className="absolute bottom-0 inset-x-0 p-3 bg-gradient-to-t from-black/70 to-transparent text-left">
            <div className="text-[10px] tracking-[0.28em] uppercase text-white/60">
              {toDisplayDate(filled.date)}
            </div>
            <div className="text-white font-light text-sm mt-0.5 tabular-nums">
              {filled.time}
            </div>
          </div>
        </button>
      ) : canCapture ? (
        <button
          type="button"
          onClick={onOpenCamera}
          data-testid={`${testId}-add`}
          className="relative w-full aspect-[4/5] rounded-lg border border-dashed border-white/15 hover:border-emerald-400/50 hover:bg-white/[0.02] transition flex flex-col items-center justify-center gap-3 group"
        >
          <div className="w-14 h-14 rounded-full border border-white/15 flex items-center justify-center group-hover:border-emerald-400/60 group-hover:text-emerald-300 transition">
            <Plus className="w-6 h-6 text-white/60 group-hover:text-emerald-300" />
          </div>
          <div className="text-[11px] tracking-[0.32em] uppercase text-white/40 group-hover:text-white/70">
            Conserva un Istante
          </div>
        </button>
      ) : (
        <div
          data-testid={`${testId}-inactive`}
          className="w-full aspect-[4/5] rounded-lg bg-white/[0.02] border border-white/5 flex items-center justify-center px-6 text-center"
        >
          <div className="text-white/40 text-sm font-light leading-relaxed">
            {inactiveMessage}
          </div>
        </div>
      )}
    </div>
  );
}

function ArchiveThumb({ label, item, onOpen, testId }) {
  if (!item) {
    return (
      <div
        data-testid={testId}
        className="aspect-square rounded-md bg-white/[0.02] border border-white/5 flex items-center justify-center text-[10px] tracking-[0.28em] uppercase text-white/25"
      >
        — {label}
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onOpen(item.id)}
      data-testid={testId}
      className="relative aspect-square rounded-md overflow-hidden border border-white/5 hover:border-white/20 transition group"
    >
      <img src={imageUrlFor(item)} alt={label} className="w-full h-full object-cover" />
      <div className="absolute bottom-0 inset-x-0 p-2 bg-gradient-to-t from-black/70 to-transparent">
        <div className="text-[9px] tracking-[0.28em] uppercase text-white/70">
          {label} · {item.time}
        </div>
      </div>
    </button>
  );
}

function PreviewDialog({ pending, busy, onCancel, onConfirm }) {
  return (
    <div
      data-testid="istanti-preview"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={busy ? undefined : onCancel}
    >
      <div
        className="glass-card w-full max-w-md p-6 text-center"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="text-[11px] tracking-[0.32em] uppercase text-emerald-300 mb-4">
          Anteprima con firma TIME Watch
        </div>
        <div className="mb-6 rounded-lg overflow-hidden border border-white/10">
          <img src={pending.previewUrl} alt="Anteprima Istante" className="w-full h-auto" />
        </div>
        <div className="text-white/70 font-light mb-6">
          Vuoi conservare questo Istante nel cloud?
        </div>
        <div className="flex items-center justify-center gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            data-testid="istanti-preview-cancel"
            className="px-5 py-2.5 rounded-full text-white/60 hover:text-white text-sm tracking-wide disabled:opacity-40"
          >
            Annulla
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            data-testid="istanti-preview-confirm"
            className="px-6 py-2.5 rounded-full bg-emerald-500/90 text-black text-sm tracking-wide font-medium hover:bg-emerald-400 transition disabled:opacity-60"
          >
            {busy ? "Caricamento…" : "Conserva l'Istante"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Viewer({ item, onClose, onDelete }) {
  const [confirmDel, setConfirmDel] = useState(false);
  return (
    <div
      data-testid="istanti-viewer"
      className="fixed inset-0 z-50 bg-black flex flex-col items-center justify-center"
      onClick={onClose}
    >
      <button
        type="button"
        onClick={onClose}
        data-testid="istanti-viewer-close"
        aria-label="Chiudi"
        className="absolute top-4 right-4 p-2 rounded-full bg-white/10 border border-white/15 text-white hover:bg-white/20 transition"
      >
        <XIcon className="w-5 h-5" />
      </button>
      <img
        src={imageUrlFor(item)}
        alt="Istante"
        className="max-w-[92vw] max-h-[72vh] object-contain rounded-lg shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      />
      <div className="mt-6 text-center" onClick={(e) => e.stopPropagation()}>
        <div className="text-[11px] tracking-[0.32em] uppercase text-white/50">
          {item.slot === "morning" ? "Istante del Mattino" : "Istante della Sera"}
        </div>
        <div className="mt-2 text-white font-light text-lg">
          {toDisplayDate(item.date)}
          <span className="text-white/40 mx-2">·</span>
          <span className="tabular-nums">{item.time}</span>
        </div>
        <div className="mt-4">
          {confirmDel ? (
            <div className="flex items-center gap-2 justify-center text-sm">
              <span className="text-white/60">Sicuro?</span>
              <button
                type="button"
                onClick={onDelete}
                data-testid="istanti-viewer-delete-confirm"
                className="px-3 py-1 rounded-full bg-red-500/80 text-white text-xs tracking-wide"
              >
                Elimina
              </button>
              <button
                type="button"
                onClick={() => setConfirmDel(false)}
                className="px-3 py-1 rounded-full text-white/60 text-xs tracking-wide"
              >
                Annulla
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => setConfirmDel(true)}
              data-testid="istanti-viewer-delete"
              className="inline-flex items-center gap-2 text-[11px] tracking-[0.24em] uppercase text-white/40 hover:text-red-300 transition"
            >
              <Trash2 className="w-3.5 h-3.5" />
              Elimina
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
